'use client'

import { useEffect, useRef, useState } from 'react'

import type { PortalOnboardingTargetStatus } from '../portal-onboarding.interface'
import { getPortalOnboardingMeasurableElement } from '../portal-onboarding.utils'

const PORTAL_ONBOARDING_TARGET_RESOLVE_TIMEOUT_MS = 300

export function usePortalOnboardingTargetElement(targetId: string | null, enabled: boolean) {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [status, setStatus] = useState<PortalOnboardingTargetStatus>('pending')

  const activeTargetId = enabled ? targetId : null
  const previousTargetIdRef = useRef<string | null>(activeTargetId)

  if (previousTargetIdRef.current !== activeTargetId) {
    previousTargetIdRef.current = activeTargetId
    setStatus('pending')
    setElement(null)
  }

  useEffect(() => {
    if (!enabled || !targetId || typeof document === 'undefined') {
      setElement(null)
      setStatus('pending')
      return
    }

    setElement(null)
    setStatus('pending')

    let frameId = 0
    const startedAt = performance.now()

    const resolve = () => {
      const found = document.querySelector<HTMLElement>(`[data-portal-onboarding="${targetId}"]`)
      const measurable = getPortalOnboardingMeasurableElement(found)

      if (measurable) {
        setElement((current) => (current === measurable ? current : measurable))
        setStatus('found')
        return
      }

      if (performance.now() - startedAt >= PORTAL_ONBOARDING_TARGET_RESOLVE_TIMEOUT_MS) {
        setElement(null)
        setStatus('missing')
        return
      }

      frameId = window.requestAnimationFrame(resolve)
    }

    resolve()

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [enabled, targetId])

  return { element, status }
}
